import { DishRowData } from "../../components/pages/costPrice/new/costPriceDataTypes";
import { CostTargets, DEFAULT_COST_TARGETS, getCostTargetForDishType } from "../../utils/costTargets";

/**
 * Menu Engineering Matrix Categories
 * Based on margin (y-axis) and volume/popularity (x-axis)
 */
export type MenuEngineeringMatrix = "Star" | "Dog" | "Puzzle" | "Plowhorse" | null;

const MIN_ITEMS_FOR_ANALYSIS = 2; // Minimum items needed for meaningful average calculation

/**
 * Calculate the menu engineering matrix classification for a single item.
 * The margin threshold is derived from the per-dish cost target
 * (food/drinks/overall) so different categories can have different bars.
 */
export function calculateMenuEngineeringMatrix(
    item: DishRowData,
    averageUnitsSold: number,
    targets: CostTargets = DEFAULT_COST_TARGETS,
): MenuEngineeringMatrix {
    // If no sales data, we cannot assign a matrix score
    if (item.popularity === 0) {
        return null;
    }

    // If item doesn't have valid cost and price data, cannot classify
    if (item.totalCost === 0 || item.price === 0 || item.isPlaceholder) {
        return null;
    }

    // Calculate margin (1 - cost price percentage)
    // marginPercentage = ((price - cost) / price) * 100
    // So margin as decimal = marginPercentage / 100
    const marginDecimal = item.marginPercentage / 100;

    // High margin = margin at or above (1 - target%), per the dish's type.
    const targetPercent = getCostTargetForDishType(item.revenueSourceType, targets);
    const marginThreshold = 1 - targetPercent / 100;
    const hasHighMargin = marginDecimal >= marginThreshold;

    // High volume = units sold >= average
    const hasHighVolume = item.popularity >= averageUnitsSold;

    // Classify into quadrants:
    // High margin + High volume = STAR (top right)
    // High margin + Low volume = PUZZLE (top left)
    // Low margin + High volume = PLOWHORSE (bottom right)
    // Low margin + Low volume = DOG (bottom left)

    if (hasHighMargin && hasHighVolume) {
        return "Star";
    } else if (hasHighMargin && !hasHighVolume) {
        return "Puzzle";
    } else if (!hasHighMargin && hasHighVolume) {
        return "Plowhorse";
    } else {
        return "Dog";
    }
}

/**
 * Calculate the average units sold for all analyzed items
 * Only includes items that have cost price analysis and sales data
 *
 * @param items - All dish items
 * @returns The average units sold
 */
export function calculateAverageUnitsSold(items: DishRowData[]): number {
    // Filter to only include items that can be analyzed:
    // - Have cost price data (not placeholder, have totalCost and price)
    // - Have sales data (popularity > 0)
    const analyzedItems = items.filter(
        item =>
            !item.isPlaceholder &&
            item.totalCost > 0 &&
            item.price > 0 &&
            item.popularity > 0
    );

    // If we don't have enough items, return 0
    if (analyzedItems.length < MIN_ITEMS_FOR_ANALYSIS) {
        return 0;
    }

    // Calculate sum of all units sold
    const totalUnitsSold = analyzedItems.reduce(
        (sum, item) => sum + item.popularity,
        0
    );

    // Return average
    return totalUnitsSold / analyzedItems.length;
}

/**
 * Apply menu engineering matrix classification to all items
 *
 * @param items - All dish items to classify
 * @returns Items with matrix classification applied
 */
export function applyMenuEngineeringMatrix(
    items: DishRowData[],
    targets: CostTargets = DEFAULT_COST_TARGETS,
): DishRowData[] {
    // Calculate average units sold across all analyzed items
    const averageUnitsSold = calculateAverageUnitsSold(items);

    // Apply matrix classification to each item
    return items.map(item => ({
        ...item,
        matrix: calculateMenuEngineeringMatrix(item, averageUnitsSold, targets),
    }));
}
